/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import {
  InputBox,
  SearchBox,
  SearchBoxRow,
  SearchButtonWrap,
  SearchCols,
  SearchRows,
} from 'components/layouts/SearchBox';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { MultiComboBox } from 'components/selects/ComboBox';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { CommonUtil } from 'utils/CommonUtil';
import CustomGrid from 'components/grids/CustomGrid';
import { hasRole } from 'utils/SessionUtil';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { findProjectStep } from 'apis/setup/SetupStep';
import { SetupStep, SetupStepCondition } from 'models/setup/SetupStep';

/**
 * @desc 설비셋업관리 > 프로젝트 기준정보 > 프로젝트 셋업단계 설정/조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.07.07   jshwang12       신규 생성
 */
const SetupProjectStepPage = () => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const initCondition = {
    bildPlntCds: [],
    elmPrjNm: '',
  };
  const [code, setCode] = useState<any>();
  const [condition, setCondition] = useState<SetupStepCondition>({ ...initCondition });
  const [rowData, setRowData] = useState<SetupStep[]>([]);
  const [hasAuth, setHasAuth] = useState<boolean>(false);

  useEffect(() => {
    getCommonCodes();
    if (hasRole('ADM') || hasRole('FA_MANAGER') || hasRole('FA_USER')) {
      setHasAuth(true);
    }
    handleSearch();
  }, []);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('FA_SU_PLNT_CD'), // 플랜트
    ]).then(([bildPlntCd]) => {
      setCode({
        bildPlntCd: bildPlntCd,
      });
    });
  };

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearch = () => {
    findProjectStep(condition).then((result) => setRowData(result || []));
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.프로젝트 셋업단계 목록', '프로젝트 셋업단계 목록'),
      t('setup.label.프로젝트 셋업단계 목록', '프로젝트 셋업단계 목록')
    );
  };

  const moveToPage = (item: any) => {
    if (item?.elmPrjId) {
      console.log('-');
      navigate(`/setup/project-step/detail`, {
        state: {
          elmPrjId: item.elmPrjId,
        },
      });
    }
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'bildPlntCdNm',
        header: String(t('setup.label.플랜트', '플랜트')),
        align: 'center',
        minWidth: 80,
        width: 100,
      },
      {
        binding: 'elmPrjNm',
        header: String(t('setup.label.프로젝트명', '프로젝트명')),
        align: 'left',
        minWidth: 120,
        width: '*',
        cellTemplate: CellMaker.makeLink({
          text: '<span>${item.elmPrjNm}</span>',
          click: (e, ctx) => {
            moveToPage(ctx.item);
          },
        }),
      },
      {
        binding: 'setgStepCdNm',
        header: String(t('setup.label.상태', '상태')),
        minWidth: 80,
        width: 89,
        align: 'center',
        cssClass: 'WijmoTag',
        cellTemplate: (item) => {
          if (item.item.setgStepCd === 'ING') {
            return `<span class="yellow">${item.value}</span>`;
          } else if (item.item.setgStepCd === 'CPLT') {
            return `<span class="grey">${item.value}</span>`;
          } else if (item.item.setgStepCd === 'ADD') {
            return `<span class="green">${item.value}</span>`;
          }
          return `<span class="blue">${t('setup.label.설정전', '설정전')}</span>`;
        },
      },
      /*{
        header: String(t('setup.label.단계설정 여부', '단계설정 여부')),
        columns: [
          {
            binding: 'spTpYn',
            header: String(t('setup.label.단계 유형', '단계 유형')),
            align: 'center',
            minWidth: 70,
            width: 100,
          },
          {
            binding: 'cmnSpYn',
            header: String(t('setup.label.공통 단계', '공통 단계')),
            align: 'center',
            minWidth: 70,
            width: 100,
          },
          {
            binding: 'spTpSpYn',
            header: String(t('setup.label.셋업 단계', '셋업 단계')),
            align: 'center',
            minWidth: 70,
            width: 100,
          },
        ],
      },*/
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        align: 'center',
        minWidth: 40,
        width: 120,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        align: 'center',
        minWidth: 40,
        width: 120,
      },
    ];
  }, [code]);
  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('setup.label.플랜트', '플랜트'))}</label>
                <MultiComboBox
                  id="bildPlntCds"
                  options={code?.bildPlntCd || []}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.bildPlntCds || []}
                  onChange={(value) => handleOnChange('bildPlntCds', value)}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.프로젝트 명', '프로젝트 명'))}</label>
                <CustomInputWithSearch
                  name="elmPrjNm"
                  placeholder={String(
                    t('setup.label.프로젝트 명을 입력해 주세요.', '프로젝트 명을 입력해 주세요.')
                  )}
                  value={condition?.elmPrjNm}
                  onChange={(e) => {
                    handleOnChange(e.target.name, e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={handleSearch}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.프로젝트 셋업단계 목록', '프로젝트 셋업단계 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={580}
        isReadOnly={true}
        initialized={onInitialized}
      />
    </>
  );
};

export default SetupProjectStepPage;
